import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  ChevronDown,
  ChevronRight,
  Search,
  RefreshCw,
  FileText,
  AlertCircle,
  Calendar,
  Package,
  Clock,
  CheckCircle2,
  Loader2,
  Truck,
  Factory,
  ShoppingCart,
  Eye,
  AlertTriangle,
  IndianRupee,
  MoreVertical,
  ArrowRightLeft,
  ClipboardList,
  Undo2,
  RotateCcw,
  History,
  ArrowRight,
  Pencil,
  XCircle,
  MapPin
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { exportToExcel } from '../utils/exportToExcel';
import { Download } from 'lucide-react';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { formatCurrency } from '@/utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// F1 — source-aware fulfillment status (per-line-item).
//   Make  -> Pending Production / Production Done / Dispatched
//   Buy   -> Pending Stock / In Stock / Dispatched
// Unknown fulfillment_type surfaces rather than defaults silently.
// Top-level so both the main component and SOLineItemRow (top-level
// component, declared outside the main closure) can call it.
function computeSourceAwareStatus(row) {
  const ordered = row.ordered_quantity || 0;
  const dispatched = row.dispatched_quantity || 0;
  if (ordered > 0 && dispatched >= ordered) {
    return { label: 'Dispatched', tone: 'bg-green-100 text-green-800 border-green-200' };
  }
  const ft = row.fulfillment_type;
  if (ft === 'Make') {
    const received = row.wo_received_qty || 0;
    if (received >= ordered && ordered > 0) {
      return { label: 'Production Done', tone: 'bg-blue-100 text-blue-800 border-blue-200' };
    }
    return { label: 'Pending Production', tone: 'bg-amber-100 text-amber-800 border-amber-200' };
  }
  if (ft === 'Buy') {
    const ready = row.ready_quantity || 0;
    const stock = row.current_stock || 0;
    if (ready > 0 && stock > 0) {
      return { label: 'In Stock', tone: 'bg-blue-100 text-blue-800 border-blue-200' };
    }
    return { label: 'Pending Stock', tone: 'bg-amber-100 text-amber-800 border-amber-200' };
  }
  return { label: `Unknown (${ft ?? 'null'})`, tone: 'bg-red-100 text-red-800 border-red-200' };
}

// Status configurations for production
const PRODUCTION_STATUS_CONFIG = {
  completed: {
    bg: 'bg-green-100',
    text: 'text-green-800',
    border: 'border-green-300',
    label: 'Completed',
    icon: CheckCircle2
  },
  in_progress: {
    bg: 'bg-blue-100',
    text: 'text-blue-800',
    border: 'border-blue-300',
    label: 'In Progress',
    icon: Loader2
  },
  pending_wo: {
    bg: 'bg-gray-100',
    text: 'text-gray-700',
    border: 'border-gray-300',
    label: 'Pending WO',
    icon: Clock
  },
  in_stock: {
    bg: 'bg-green-100',
    text: 'text-green-800',
    border: 'border-green-300',
    label: 'In Stock',
    icon: Package
  },
  partial_stock: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
    border: 'border-yellow-300',
    label: 'Partial Stock',
    icon: AlertTriangle
  },
  no_stock: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    border: 'border-red-300',
    label: 'No Stock',
    icon: AlertCircle
  }
};

// Process status configurations
const PROCESS_STATUS_CONFIG = {
  completed: { bg: 'bg-green-500', text: 'text-white', label: 'Completed' },
  partial: { bg: 'bg-yellow-500', text: 'text-white', label: 'Partial' },
  in_process: { bg: 'bg-blue-500', text: 'text-white', label: 'In Process' },
  issued: { bg: 'bg-purple-500', text: 'text-white', label: 'Issued' },
  pending: { bg: 'bg-gray-400', text: 'text-white', label: 'Pending' }
};

// Fulfillment status configurations (for status badge)
const FULFILLMENT_STATUS_CONFIG = {
  ready: { bg: 'bg-green-100', text: 'text-green-800', label: '🟢 Ready', icon: CheckCircle2 },
  plan_pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: '🟡 Plan Pending', icon: ClipboardList },
  partial_dispatched: { bg: 'bg-blue-100', text: 'text-blue-800', label: '🔵 Partial', icon: Truck },
  fully_dispatched: { bg: 'bg-gray-100', text: 'text-gray-700', label: '✅ Dispatched', icon: CheckCircle2 },
  in_progress: { bg: 'bg-purple-100', text: 'text-purple-800', label: '⚙️ In Progress', icon: Loader2 }
};

// Format helpers
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return format(date, 'dd/MM/yy');
  } catch {
    return '-';
  }
};

// Fulfillment Status Badge Component
const FulfillmentStatusBadge = ({ status }) => {
  const config = FULFILLMENT_STATUS_CONFIG[status] || FULFILLMENT_STATUS_CONFIG.in_progress;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

// In Plans Tooltip Component
const InPlansTooltip = ({ plans, quantity }) => {
  if (!quantity || quantity === 0) {
    return <span className="text-gray-400">0</span>;
  }
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-yellow-600 font-medium cursor-pointer underline decoration-dotted">
            {quantity}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-sm p-3">
          <div className="text-sm">
            <p className="font-semibold mb-2">Pending Dispatch Plans:</p>
            <div className="space-y-2">
              {plans && plans.map((p, idx) => (
                <div key={idx} className="flex items-center justify-between gap-3 py-1 border-b border-gray-100 last:border-0">
                  <span className="font-mono text-xs">{p.plan_number}</span>
                  <span className="text-xs">{p.actual_quantity > 0 ? p.actual_quantity : p.planned_quantity} qty</span>
                  <Badge variant="outline" className="text-xs capitalize">{p.status}</Badge>
                </div>
              ))}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Reallocated Indicator Component  
const ReallocatedIndicator = ({ inQty, outQty }) => {
  if (!inQty && !outQty) return null;
  
  return (
    <span className="text-xs ml-1">
      {inQty > 0 && (
        <span className="text-blue-600" title="Reallocated from other SO">+{inQty}</span>
      )}
      {outQty > 0 && (
        <span className="text-orange-600" title="Reallocated to other SO">-{outQty}</span>
      )}
    </span>
  );
};

// Enhancement 2: Delay Status Badge
const DelayStatusBadge = ({ isDelayed, isFullyDispatched }) => {
  if (isFullyDispatched) {
    return null; // Don't show for dispatched items
  }
  
  if (isDelayed) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
        Delayed
      </span>
    );
  }
  
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
      On Track
    </span>
  );
};

// Enhancement 4: Get delivery date color based on days remaining and delay status
const getDeliveryDateColor = (daysToDelivery, isDelayed, isFullyDispatched) => {
  // Grey for fully dispatched
  if (isFullyDispatched) {
    return 'text-gray-400 bg-gray-50';
  }
  
  // Red: <3 days remaining OR any process delayed
  if (daysToDelivery !== null && daysToDelivery < 3) {
    return 'text-red-600 bg-red-50 font-medium';
  }
  if (isDelayed) {
    return 'text-red-600 bg-red-50 font-medium';
  }
  
  // Amber: 3-7 days remaining
  if (daysToDelivery !== null && daysToDelivery >= 3 && daysToDelivery <= 7) {
    return 'text-amber-600 bg-amber-50';
  }
  
  // Green: >7 days remaining and not delayed
  if (daysToDelivery !== null && daysToDelivery > 7) {
    return 'text-green-600 bg-green-50';
  }
  
  return 'text-gray-600';
};

// Enhancement 5: Delivery Date Display with Original Date (if changed)
const DeliveryDateDisplay = ({ deliveryDate, originalDeliveryDate, dateChangeHistory, daysToDelivery, isDelayed, isFullyDispatched }) => {
  const dateColorClass = getDeliveryDateColor(daysToDelivery, isDelayed, isFullyDispatched);
  const hasDateChanged = originalDeliveryDate && originalDeliveryDate !== deliveryDate;
  
  // Format history for tooltip
  const formatHistoryTooltip = () => {
    if (!dateChangeHistory || dateChangeHistory.length === 0) {
      return `Changed from ${formatDate(originalDeliveryDate)}`;
    }
    
    return dateChangeHistory.map((change, idx) => 
      `${formatDate(change.old_value)} → ${formatDate(change.new_value)} by ${change.changed_by} on ${formatDate(change.changed_at)}`
    ).join('\n');
  };
  
  return (
    <div className="text-center">
      {hasDateChanged ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="cursor-help">
                <div className="text-xs text-gray-400 line-through">
                  {formatDate(originalDeliveryDate)}
                </div>
                <div className={`text-xs px-1 py-0.5 rounded ${dateColorClass}`}>
                  {formatDate(deliveryDate)}
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs p-2">
              <div className="text-xs">
                <p className="font-semibold mb-1">Date Change History:</p>
                {dateChangeHistory && dateChangeHistory.length > 0 ? (
                  dateChangeHistory.map((change, idx) => (
                    <div key={idx} className="py-1 border-b border-gray-100 last:border-0">
                      <div>{formatDate(change.old_value)} → {formatDate(change.new_value)}</div>
                      <div className="text-gray-500">by {change.changed_by}</div>
                    </div>
                  ))
                ) : (
                  <div>Original: {formatDate(originalDeliveryDate)}</div>
                )}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <div className={`text-xs px-1 py-0.5 rounded ${dateColorClass}`}>
          {formatDate(deliveryDate)}
        </div>
      )}
      {daysToDelivery !== null && !isFullyDispatched && (
        <div className={`text-xs mt-0.5 ${daysToDelivery < 0 ? 'text-red-500 font-medium' : daysToDelivery <= 3 ? 'text-amber-600' : 'text-gray-400'}`}>
          {daysToDelivery < 0 ? `${Math.abs(daysToDelivery)}d overdue` : `${daysToDelivery}d left`}
        </div>
      )}
    </div>
  );
};

// Enhancement 1: Process Progress Table Component (CRM Enhancement)
const ProcessProgressTable = ({ processes }) => {
  if (!processes || processes.length === 0) {
    return <p className="text-sm text-gray-500 py-2">No process data available</p>;
  }

  const getStatusDisplay = (process) => {
    const displayStatus = process.display_status || 'not_started';
    
    switch (displayStatus) {
      case 'done':
        return {
          icon: '✅',
          label: 'Done',
          className: 'text-green-700 bg-green-50'
        };
      case 'in_production':
        return {
          icon: '🔄',
          label: 'In Production',
          className: 'text-blue-700 bg-blue-50'
        };
      default:
        return {
          icon: '⏳',
          label: 'Not Started',
          className: 'text-gray-600 bg-gray-50'
        };
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-100 text-slate-600">
            <th className="text-left py-2 px-3 font-medium">Process</th>
            <th className="text-center py-2 px-2 font-medium">Issued</th>
            <th className="text-center py-2 px-2 font-medium">Completed</th>
            <th className="text-center py-2 px-3 font-medium">Status</th>
            <th className="text-center py-2 px-2 font-medium">Target Date</th>
            <th className="text-center py-2 px-2 font-medium">Planned Date</th>
            <th className="text-center py-2 px-2 font-medium">Actual Date</th>
          </tr>
        </thead>
        <tbody>
          {processes.map((proc, idx) => {
            const statusInfo = getStatusDisplay(proc);
            const isDelayed = proc.is_delayed;
            
            return (
              <tr 
                key={idx} 
                className={`border-b border-gray-100 ${isDelayed ? 'bg-red-50' : ''}`}
              >
                <td className="py-2 px-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{proc.process_name}</span>
                    {proc.is_outsourced && (
                      <Badge variant="outline" className="text-xs">OJC</Badge>
                    )}
                  </div>
                </td>
                <td className="text-center py-2 px-2 font-mono">
                  {proc.issued_qty || 0}
                </td>
                <td className="text-center py-2 px-2 font-mono">
                  {proc.completed_qty || 0}
                </td>
                <td className="text-center py-2 px-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${statusInfo.className}`}>
                    {statusInfo.icon} {statusInfo.label}
                  </span>
                </td>
                <td className={`text-center py-2 px-2 ${isDelayed ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                  {formatDate(proc.benchmark_date) || '-'}
                  {isDelayed && <span className="ml-1 text-red-500">⚠️</span>}
                </td>
                <td className="text-center py-2 px-2 text-gray-600">
                  {formatDate(proc.planned_date) || '-'}
                </td>
                <td className="text-center py-2 px-2">
                  {proc.display_status === 'done' && proc.completion_date ? (
                    <span className="text-green-600 font-medium">{formatDate(proc.completion_date)}</span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// Process Row Component for WO expansion (Legacy - keeping for backward compatibility)
const ProcessRow = ({ process, isLast }) => {
  const status = PROCESS_STATUS_CONFIG[process.status] || PROCESS_STATUS_CONFIG.pending;
  
  return (
    <div className={`flex items-center py-2 px-4 ${!isLast ? 'border-b border-gray-100' : ''}`}>
      <div className="w-40 flex items-center gap-2">
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${status.bg} ${status.text}`}>
          {process.process_name}
        </span>
        {process.is_outsourced && (
          <Badge variant="outline" className="text-xs">OJC</Badge>
        )}
      </div>
      <div className="w-20 text-center text-sm">
        {process.completed_qty || 0}/{process.target_qty}
      </div>
      <div className="w-24 text-center">
        <span className={`text-xs ${status.bg} ${status.text} px-2 py-0.5 rounded`}>
          {status.label}
        </span>
      </div>
      <div className="w-20 text-center text-xs text-gray-600">{formatDate(process.benchmark_date)}</div>
      <div className="w-20 text-center text-xs text-gray-600">{formatDate(process.planned_date)}</div>
      <div className="w-20 text-center text-xs">
        {process.status === 'completed' ? (
          <span className="text-green-600">{formatDate(process.completion_date)}</span>
        ) : '-'}
      </div>
    </div>
  );
};

// Work Order Expandable Row
const WorkOrderExpandable = ({ wo }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!wo) return null;
  
  const status = PROCESS_STATUS_CONFIG[wo.status] || PROCESS_STATUS_CONFIG.pending;
  
  return (
    <div className="border rounded-lg bg-white mt-2">
      <div 
        className="flex items-center py-2 px-3 cursor-pointer hover:bg-gray-50"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="w-5">
          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </div>
        <div className="flex-1 text-sm font-medium">{wo.work_order_number}</div>
        <Badge className={`${status.bg} ${status.text}`}>{status.label}</Badge>
      </div>
      
      {isExpanded && wo.processes && wo.processes.length > 0 && (
        <div className="border-t p-3">
          {/* Enhancement 1: Use new ProcessProgressTable */}
          <ProcessProgressTable processes={wo.processes} />
        </div>
      )}
    </div>
  );
};

// Item Row Component for All/In Progress tabs
const ItemRow = ({ item, isExpanded, onToggle, onReallocate, canTakeActions }) => {
  const status = PRODUCTION_STATUS_CONFIG[item.production_status] || PRODUCTION_STATUS_CONFIG.pending_wo;
  const StatusIcon = status.icon;
  
  return (
    <div className="border-b last:border-b-0">
      <div 
        className={`flex items-center py-3 px-4 hover:bg-gray-50 ${item.is_overdue || item.is_any_process_delayed ? 'bg-red-50/50' : ''}`}
      >
        <div className="w-6 cursor-pointer" onClick={onToggle}>
          {item.work_order ? (
            isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
          ) : <div className="w-4" />}
        </div>
        
        {/* SO & Client */}
        <div className="w-28 cursor-pointer" onClick={onToggle}>
          <div className="font-medium text-sm">{item.so_number}</div>
          <div className="text-xs text-gray-500 truncate" title={item.client_name}>{item.client_name}</div>
        </div>
        
        {/* Item */}
        <div className="flex-1 min-w-0 px-2 cursor-pointer" onClick={onToggle}>
          <div className="font-medium text-sm truncate" title={item.item_name}>{item.item_name}</div>
          <div className="text-xs text-gray-500">{item.item_code}</div>
        </div>
        
        {/* Type */}
        <div className="w-14 text-center">
          <Badge variant={item.fulfillment_type === 'Make' ? 'default' : 'secondary'} className="text-xs">
            {item.fulfillment_type === 'Make' ? <Factory className="w-3 h-3 mr-1" /> : <ShoppingCart className="w-3 h-3 mr-1" />}
            {item.fulfillment_type}
          </Badge>
        </div>
        
        {/* Quantities */}
        <div className="w-14 text-center text-sm">
          <span className="font-medium">{item.ordered_quantity}</span>
        </div>
        <div className="w-14 text-center text-sm">
          <span className={item.dispatched_quantity > 0 ? 'text-blue-600 font-medium' : 'text-gray-400'}>
            {item.dispatched_quantity}
          </span>
        </div>
        
        {/* In Plans */}
        <div className="w-14 text-center text-sm">
          <InPlansTooltip plans={item.pending_plans} quantity={item.in_plans_qty} />
        </div>
        
        {/* Ready Qty */}
        <div className="w-14 text-center text-sm">
          <span className={item.ready_quantity > 0 ? 'text-green-600 font-medium' : 'text-gray-400'}>
            {item.ready_quantity}
          </span>
          <ReallocatedIndicator inQty={item.reallocated_in_qty} outQty={item.reallocated_out_qty} />
        </div>
        
        {/* Enhancement 2: Expected Ready Date */}
        <div className="w-20 text-center text-xs text-gray-600">
          {item.expected_ready_date ? formatDate(item.expected_ready_date) : '-'}
        </div>
        
        {/* Enhancement 2: Delay Status */}
        <div className="w-20 text-center">
          <DelayStatusBadge isDelayed={item.is_any_process_delayed} isFullyDispatched={item.is_fully_dispatched} />
        </div>
        
        {/* Enhancement 4 & 5: Delivery Date with Color Coding and Change History */}
        <div className="w-24">
          <DeliveryDateDisplay 
            deliveryDate={item.delivery_date}
            originalDeliveryDate={item.original_delivery_date}
            dateChangeHistory={item.date_change_history}
            daysToDelivery={item.days_to_delivery}
            isDelayed={item.is_any_process_delayed}
            isFullyDispatched={item.is_fully_dispatched}
          />
        </div>
        
        {/* Actions Menu */}
        <div className="w-10 text-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canTakeActions && item.ready_quantity > 0 && (
                <DropdownMenuItem onClick={() => onReallocate && onReallocate(item)}>
                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                  Reallocate Stock
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={onToggle}>
                <Eye className="w-4 h-4 mr-2" />
                View Details
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      {/* Expanded WO Details */}
      {isExpanded && item.work_order && (
        <div className="px-10 pb-3">
          <WorkOrderExpandable wo={item.work_order} />
        </div>
      )}
    </div>
  );
};

// SO Accordion Row for grouped view
const SOAccordionRow = ({ soGroup, isExpanded, onToggle, onReallocate, canTakeActions, canSeePrices }) => {
  // Count items that are fully ready (ready_qty covers remaining demand)
  const fullyReadyCount = soGroup.line_items.filter(li => {
    const remaining = (li.ordered_quantity || 0) - (li.dispatched_quantity || 0);
    return li.ready_quantity >= remaining && remaining > 0;
  }).length;
  // Count items with any ready qty (partially or fully ready)
  const hasReadyCount = soGroup.line_items.filter(li => li.ready_quantity > 0).length;
  const totalCount = soGroup.line_items.length;
  const allReady = fullyReadyCount === totalCount && totalCount > 0;
  const someReady = hasReadyCount > 0 && !allReady;
  const isOverdue = soGroup.is_overdue;
  
  return (
    <React.Fragment>
      <div 
        className={`flex items-center py-3 px-4 border-b cursor-pointer hover:bg-gray-50 transition-colors ${allReady ? 'bg-green-50' : isOverdue ? 'bg-red-50' : ''}`}
        onClick={onToggle}
      >
        <div className="w-6">
          {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
        </div>
        <div className="w-40">
          <div className="font-bold text-sm">{soGroup.so_number}</div>
          <div className="text-xs text-gray-500 truncate">{soGroup.client_name}</div>
        </div>
        <div className="flex-1 flex items-center gap-3">
          {/* Readiness score */}
          <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-bold ${allReady ? 'bg-green-100 text-green-700' : someReady ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
            {fullyReadyCount}/{totalCount} ready
          </div>
          {/* Payment indicator - only show if user can see prices */}
          {canSeePrices && soGroup.so_total_advance > 0 && (
            <div className="text-xs text-gray-500">
              Paid: {formatCurrency(soGroup.so_total_advance)}
              {soGroup.so_balance_due > 0 && <span className="text-red-500 ml-1">Due: {formatCurrency(soGroup.so_balance_due)}</span>}
            </div>
          )}
        </div>
        <div className="w-16 text-center text-sm font-medium">{soGroup.total_items} items</div>
        <div className="w-24 text-center">
          <DeliveryDateDisplay 
            deliveryDate={soGroup.delivery_date}
            originalDeliveryDate={soGroup.original_delivery_date}
            dateChangeHistory={soGroup.date_change_history}
            daysToDelivery={soGroup.days_to_delivery}
            isDelayed={false}
            isFullyDispatched={false}
          />
        </div>
      </div>
      
      {/* Expanded line items */}
      {isExpanded && (
        <div className="bg-gray-50 border-b">
          <div className="flex items-center py-1.5 px-4 pl-10 bg-gray-100 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">
            <div className="w-5"></div>
            <div className="flex-1 min-w-[120px]">Item</div>
            <div className="w-16 text-center">Type</div>
            <div className="w-16 text-center">Ordered</div>
            <div className="w-20 text-center">Dispatched</div>
            <div className="w-16 text-center">In Plans</div>
            <div className="w-14 text-center">Ready</div>
            <div className="w-20 text-center">Expected</div>
            <div className="w-16 text-center">Delay</div>
            <div className="w-20 text-center">Status</div>
            <div className="w-8"></div>
          </div>
          {soGroup.line_items.map(item => (
            <SOLineItemRow 
              key={item.line_item_id} 
              item={item} 
              canTakeActions={canTakeActions}
              onReallocate={onReallocate}
            />
          ))}
        </div>
      )}
    </React.Fragment>
  );
};

// Individual Line Item Row within SO Accordion (with expandable WO details)
const SOLineItemRow = ({ item, canTakeActions, onReallocate }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <div className="border-b border-gray-100">
      <div className="flex items-center py-2 px-4 pl-10 text-sm hover:bg-white whitespace-nowrap">
        {/* Expand toggle for WO details */}
        <div className="w-5 cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
          {item.work_order ? (
            isExpanded ? <ChevronDown className="w-3 h-3 text-gray-400" /> : <ChevronRight className="w-3 h-3 text-gray-400" />
          ) : <div className="w-3" />}
        </div>
        <div className="flex-1 min-w-[120px] cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
          <div className="font-medium text-sm truncate">{item.item_name}</div>
          <div className="text-xs text-gray-400 truncate">{item.item_code}</div>
        </div>
        <div className="w-16 text-center">
          <Badge variant={item.fulfillment_type === 'Make' ? 'default' : 'secondary'} className="text-xs">
            {item.fulfillment_type === 'Make' ? 'Make' : 'Buy'}
          </Badge>
        </div>
        <div className="w-16 text-center text-sm">{item.ordered_quantity}</div>
        <div className="w-20 text-center text-sm text-blue-600">{item.dispatched_quantity}</div>
        <div className="w-16 text-center text-sm">
          <InPlansTooltip plans={item.pending_plans} quantity={item.in_plans_qty} />
        </div>
        <div className="w-14 text-center">
          <span className={`font-bold ${item.ready_quantity > 0 ? 'text-green-600' : 'text-gray-400'}`}>
            {item.ready_quantity}
          </span>
        </div>
        {/* Expected Ready Date */}
        <div className="w-20 text-center text-xs text-gray-600">
          {item.expected_ready_date ? formatDate(item.expected_ready_date) : '-'}
        </div>
        {/* Delay Status */}
        <div className="w-16 text-center">
          <DelayStatusBadge isDelayed={item.is_any_process_delayed} isFullyDispatched={item.is_fully_dispatched} />
        </div>
        <div className="w-20 text-center">
          <FulfillmentStatusBadge status={item.fulfillment_status} />
        </div>
        {/* F1 — source-aware Production Status (Make: Pending Production / Production Done / Dispatched;
            Buy: Pending Stock / In Stock / Dispatched). Distinct from the backend
            'fulfillment_status' badge immediately to the left. */}
        <div className="w-28 text-center">
          {(() => {
            const s = computeSourceAwareStatus(item);
            return (
              <span
                className={`inline-block rounded border px-1.5 py-0.5 text-[10px] font-medium ${s.tone}`}
                data-testid={`line-source-aware-status-${item.line_item_id}`}
              >
                {s.label}
              </span>
            );
          })()}
        </div>
        <div className="w-8">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={(e) => e.stopPropagation()}>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canTakeActions && item.ready_quantity > 0 && (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onReallocate(item); }}>
                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                  Reallocate Stock
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => setIsExpanded(!isExpanded)}>
                <Eye className="w-4 h-4 mr-2" />
                View Details
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      {/* Expandable WO Process Details */}
      {isExpanded && item.work_order && (
        <div className="px-12 pb-3">
          <WorkOrderExpandable wo={item.work_order} />
        </div>
      )}
    </div>
  );
};

// Ready Item Row with Checkbox
const ReadyItemRow = ({ item, isSelected, onSelect, disabled, canSeePrices, canTakeActions }) => {
  return (
    <TableRow className={`${item.is_overdue ? 'bg-red-50' : ''} ${isSelected ? 'bg-blue-50' : ''}`}>
      {canTakeActions && (
        <TableCell className="w-10">
          <Checkbox 
            checked={isSelected}
            onCheckedChange={(checked) => onSelect(item, checked)}
            disabled={disabled}
            data-testid={`select-item-${item.line_item_id}`}
          />
        </TableCell>
      )}
      <TableCell>
        <div className="font-medium">{item.so_number}</div>
        <div className="text-xs text-gray-500">{item.client_name}</div>
      </TableCell>
      <TableCell>
        <div className="font-medium">{item.item_name}</div>
        <div className="text-xs text-gray-500">{item.item_code}</div>
      </TableCell>
      <TableCell className="text-center">
        <Badge variant={item.fulfillment_type === 'Make' ? 'default' : 'secondary'} className="text-xs">
          {item.fulfillment_type}
        </Badge>
      </TableCell>
      <TableCell className="text-center font-medium">{item.ordered_quantity}</TableCell>
      <TableCell className="text-center text-blue-600">{item.dispatched_quantity}</TableCell>
      <TableCell className="text-center">
        <InPlansTooltip plans={item.pending_plans} quantity={item.in_plans_qty} />
      </TableCell>
      <TableCell className="text-center">
        <span className="text-green-600 font-bold">{item.ready_quantity}</span>
        <ReallocatedIndicator inQty={item.reallocated_in_qty} outQty={item.reallocated_out_qty} />
      </TableCell>
      {canSeePrices && <TableCell className="text-center">{formatCurrency(item.ready_value)}</TableCell>}
      <TableCell className="text-center">
        <FulfillmentStatusBadge status={item.fulfillment_status} />
      </TableCell>
      <TableCell className="text-center">
        <div className={`text-sm ${item.is_overdue ? 'text-red-600 font-medium' : ''}`}>
          {formatDate(item.delivery_date)}
        </div>
        {item.days_to_delivery !== null && item.days_to_delivery < 0 && (
          <div className="text-xs text-red-500">{Math.abs(item.days_to_delivery)}d overdue</div>
        )}
      </TableCell>
    </TableRow>
  );
};

// Dispatch History Row
const DispatchRow = ({ dispatch, onView, canSeePrices }) => {
  return (
    <TableRow>
      <TableCell className="font-mono font-medium">{dispatch.dispatch_number}</TableCell>
      <TableCell>{formatDate(dispatch.dispatch_date)}</TableCell>
      <TableCell>{dispatch.so_number}</TableCell>
      <TableCell>{dispatch.customer_name || dispatch.client_name}</TableCell>
      <TableCell>{dispatch.invoice_number || '-'}</TableCell>
      {canSeePrices && <TableCell className="text-right">{formatCurrency(dispatch.total_amount || dispatch.invoice_amount)}</TableCell>}
      <TableCell className="text-right">
        <Button size="sm" variant="ghost" onClick={() => onView(dispatch)}>
          <Eye className="w-4 h-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
};

// Main Page Component
const SOFulfillmentPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Permission: Can see prices only if user has sales_orders module access or is owner
  const canSeePrices = user?.allowed_modules?.includes('sales_orders') || user?.is_owner;
  
  // Permission: Can take actions (dispatch, reallocate, edit plans) only if user has sales_orders module access or is owner
  const canTakeActions = user?.allowed_modules?.includes('sales_orders') || user?.is_owner;
  
  // State
  const [activeTab, setActiveTab] = useState('all');  const [data, setData] = useState({ summary: {}, items: [] });
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientFilter, setClientFilter] = useState('all');
  // F1 — additional filters applied across all tabs.
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [itemFilter, setItemFilter] = useState('all');  // client-side filter on loaded data
  const [clients, setClients] = useState([]);
  
  // Ready tab state
  const [selectedItems, setSelectedItems] = useState([]);
  const [showDispatchDialog, setShowDispatchDialog] = useState(false);
  const [dispatchQuantities, setDispatchQuantities] = useState({});
  const [plannedDate, setPlannedDate] = useState(new Date().toISOString().split('T')[0]);
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  
  // Direct dispatch state
  const [isDirectDispatch, setIsDirectDispatch] = useState(false);
  const [supplierPOs, setSupplierPOs] = useState([]);
  const [selectedSupplierPO, setSelectedSupplierPO] = useState('');
  const [loadingPOs, setLoadingPOs] = useState(false);
  
  // Dispatched tab state
  const [viewDispatch, setViewDispatch] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  
  // Expanded items for All/In Progress tabs
  const [expandedItem, setExpandedItem] = useState(null); // Single expanded item (accordion style)
  const [expandedSO, setExpandedSO] = useState(null); // Expanded SO for accordion view
  
  // Reallocation state
  const [showReallocateDialog, setShowReallocateDialog] = useState(false);
  const [reallocateItem, setReallocateItem] = useState(null);
  const [eligibleDestinations, setEligibleDestinations] = useState([]);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [reallocateQty, setReallocateQty] = useState(0);
  const [reallocateReason, setReallocateReason] = useState('');
  const [reallocating, setReallocating] = useState(false);
  const [loadingDestinations, setLoadingDestinations] = useState(false);
  
  // Reallocations tab state
  const [reallocations, setReallocations] = useState([]);
  const [reallocationsLoading, setReallocationsLoading] = useState(false);
  const [reallocationsSummary, setReallocationsSummary] = useState({ total_reallocations: 0 });

  // Dispatch Plans Management state (for Stage Reset)
  const [dispatchPlans, setDispatchPlans] = useState([]);
  const [dispatchPlansLoading, setDispatchPlansLoading] = useState(false);
  const [dispatchPlansFilter, setDispatchPlansFilter] = useState('all');
  const [showStageResetDialog, setShowStageResetDialog] = useState(false);
  const [resetPlan, setResetPlan] = useState(null);
  const [resetTargetStatus, setResetTargetStatus] = useState('');
  const [resetReason, setResetReason] = useState('');
  const [resettingStage, setResettingStage] = useState(false);
  const [showResetHistoryDialog, setShowResetHistoryDialog] = useState(false);
  const [resetHistory, setResetHistory] = useState([]);

  // Dispatch Plan CRUD state (View/Edit/Cancel)
  const [showViewPlanDialog, setShowViewPlanDialog] = useState(false);
  const [viewPlanData, setViewPlanData] = useState(null);
  const [loadingPlanDetails, setLoadingPlanDetails] = useState(false);
  const [showEditPlanDialog, setShowEditPlanDialog] = useState(false);
  const [editPlanData, setEditPlanData] = useState(null);
  const [editingPlan, setEditingPlan] = useState(false);
  const [showCancelPlanDialog, setShowCancelPlanDialog] = useState(false);
  const [cancelPlanData, setCancelPlanData] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancellingPlan, setCancellingPlan] = useState(false);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  // Fetch locations for dispatch plan
  const fetchLocations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/locations`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const result = await response.json();
        setLocations(result);
        if (result.length > 0) {
          setSelectedLocation(result[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  };

  // Fetch main data
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ tab: activeTab });
      if (clientFilter !== 'all') params.append('client_id', clientFilter);
      if (searchTerm) params.append('search', searchTerm);
      // F1 — optional date-range params already supported by backend (reports.py:679-711).
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      
      const response = await fetch(`${API_URL}/api/reports/so-fulfillment?${params}`, {
        headers: getAuthHeader()
      });
      if (!response.ok) throw new Error('Failed to fetch data');
      const result = await response.json();
      setData(result);
      
      // Extract unique clients for filter
      const uniqueClients = [...new Map(
        result.items.map(item => [item.client_id, { id: item.client_id, name: item.client_name }])
      ).values()];
      setClients(uniqueClients);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch dispatch history
  const fetchDispatches = async () => {
    try {
      const params = new URLSearchParams();
      if (clientFilter !== 'all') params.append('client_id', clientFilter);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await fetch(`${API_URL}/api/reports/dispatch-history?${params}`, {
        headers: getAuthHeader()
      });
      if (!response.ok) throw new Error('Failed to fetch dispatches');
      const result = await response.json();
      setDispatches(result);
    } catch (err) {
      console.error('Error fetching dispatches:', err);
    }
  };

  // Fetch reallocations
  const fetchReallocations = async () => {
    setReallocationsLoading(true);
    try {
      const [listRes, summaryRes] = await Promise.all([
        fetch(`${API_URL}/api/reallocations/`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/api/reallocations/summary`, { headers: getAuthHeader() })
      ]);
      
      if (listRes.ok) {
        const list = await listRes.json();
        setReallocations(list);
      }
      if (summaryRes.ok) {
        const summary = await summaryRes.json();
        setReallocationsSummary(summary);
      }
    } catch (err) {
      console.error('Error fetching reallocations:', err);
    } finally {
      setReallocationsLoading(false);
    }
  };

  // Fetch eligible destinations for reallocation
  const fetchEligibleDestinations = async (itemId, excludeSoId) => {
    setLoadingDestinations(true);
    try {
      const params = new URLSearchParams();
      if (excludeSoId) params.append('exclude_sales_order_id', excludeSoId);
      
      const response = await fetch(
        `${API_URL}/api/reallocations/eligible-sources/${itemId}?${params}`,
        { headers: getAuthHeader() }
      );
      if (response.ok) {
        const sources = await response.json();
        // Filter to show only other SOs that need this item (not have stock)
        setEligibleDestinations(sources);
      }
    } catch (err) {
      console.error('Error fetching eligible destinations:', err);
    } finally {
      setLoadingDestinations(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchLocations();
    if (activeTab === 'dispatched') {
      fetchDispatches();
    }
    if (activeTab === 'reallocations') {
      fetchReallocations();
    }
    if (activeTab === 'plans') {
      fetchDispatchPlans();
    }
  }, [activeTab, clientFilter, dispatchPlansFilter, fromDate, toDate]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData();
      if (activeTab === 'dispatched') fetchDispatches();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Toggle item expansion
  const toggleExpand = (itemId) => {
    setExpandedItem(prev => prev === itemId ? null : itemId);
  };

  // ------------------------------------------------------------------
  // F1 — client-side item filter (dropdown) + unique items list.
  //   Item filter narrows the rows visible in all tabs without a
  //   backend round-trip. Data already flat per `/so-fulfillment`
  //   response; see reports.py:1057-1062.
  // ------------------------------------------------------------------
  const uniqueItems = useMemo(() => {
    const seen = new Map();
    for (const row of (data.items || [])) {
      if (!seen.has(row.item_id)) {
        seen.set(row.item_id, {
          id: row.item_id,
          item_code: row.item_code || '',
          item_name: row.item_name || '',
        });
      }
    }
    return Array.from(seen.values()).sort((a, b) =>
      (a.item_code || a.item_name).localeCompare(b.item_code || b.item_name),
    );
  }, [data.items]);

  const filteredItems = useMemo(() => {
    const list = data.items || [];
    if (itemFilter === 'all') return list;
    return list.filter((r) => r.item_id === itemFilter);
  }, [data.items, itemFilter]);

  // F1 — delegate to top-level computeSourceAwareStatus (lifted so
  // top-level row components at file scope can reuse the same logic).
  const computeProductionStatus = computeSourceAwareStatus;

  // ------------------------------------------------------------------
  // F1 — tab-aware Excel export. SO-level tabs export SO-grouped summary
  // rows; Line Items tab exports the flat per-line dataset.
  // ------------------------------------------------------------------
  const handleExportClick = () => {
    const fromLbl = fromDate || 'all';
    const toLbl = toDate || 'all';
    const filenameBase = `so_fulfillment_${activeTab}_${fromLbl}_to_${toLbl}.xlsx`;
    if (activeTab === 'line_items') {
      const rows = (filteredItems || []).map((r) => ({
        'SO Number': r.so_number || '',
        'Customer': r.client_name || '',
        'Item Code': r.item_code || '',
        'Item Name': r.item_name || '',
        'Fulfillment Type': r.fulfillment_type || '',
        'Ordered Qty': r.ordered_quantity || 0,
        'Dispatched Qty': r.dispatched_quantity || 0,
        'Pending Qty': Math.max(0, (r.ordered_quantity || 0) - (r.dispatched_quantity || 0)),
        'Target Production Date': r.target_production_date || '',
        'Production Status': computeProductionStatus(r).label,
        'WO Numbers': (r.wo_numbers || []).join(', '),
        'Indent Numbers': (r.indent_numbers || []).join(', '),
        'Last Dispatch Date': r.last_dispatch_date || '',
      }));
      if (rows.length === 0) { toast.info('Nothing to export'); return; }
      exportToExcel(rows, 'Line Items', filenameBase);
      return;
    }
    // SO-level tabs: flatten groupedBySO into summary rows. groupedBySO is
    // a useMemo declared later; rebuild equivalently here from filteredItems.
    const soMap = new Map();
    for (const r of filteredItems) {
      const k = r.sales_order_id;
      if (!soMap.has(k)) {
        soMap.set(k, {
          so_number: r.so_number || '',
          customer_name: r.client_name || '',
          so_status: r.so_status || '',
          ordered_at: r.ordered_at || '',
          ordered_qty: 0,
          dispatched_qty: 0,
          total_value: 0,
          line_items_count: 0,
          target_production_date: r.target_production_date || '',
        });
      }
      const agg = soMap.get(k);
      agg.ordered_qty += r.ordered_quantity || 0;
      agg.dispatched_qty += r.dispatched_quantity || 0;
      agg.total_value += (r.ordered_quantity || 0) * (r.unit_price || 0);
      agg.line_items_count += 1;
    }
    const rows = Array.from(soMap.values()).map((s) => ({
      'SO Number': s.so_number,
      'Customer': s.customer_name,
      'Status': s.so_status,
      'Ordered At': s.ordered_at,
      'Ordered Qty (sum)': s.ordered_qty,
      'Dispatched Qty (sum)': s.dispatched_qty,
      'Pending Qty (sum)': Math.max(0, s.ordered_qty - s.dispatched_qty),
      'Total Value (sum)': s.total_value,
      'Line Items': s.line_items_count,
      'Target Production Date': s.target_production_date,
    }));
    if (rows.length === 0) { toast.info('Nothing to export'); return; }
    exportToExcel(rows, `SO Summary - ${activeTab}`, filenameBase);
  };

  // Handle reallocate action - opens reallocation dialog
  const handleReallocate = (item) => {
    setReallocateItem(item);
    setReallocateQty(Math.min(item.ready_quantity, 1)); // Default to 1
    setReallocateReason('');
    setSelectedDestination(null);
    setEligibleDestinations([]);
    setShowReallocateDialog(true);
    
    // Fetch SOs that need this item
    fetchEligibleDestinations(item.item_id, item.sales_order_id);
  };

  // Execute reallocation
  const executeReallocation = async () => {
    if (!selectedDestination) {
      toast.error('Please select a destination SO');
      return;
    }
    if (!reallocateQty || reallocateQty <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    if (!reallocateReason.trim()) {
      toast.error('Please provide a reason for reallocation');
      return;
    }
    
    setReallocating(true);
    try {
      const payload = {
        source_so_id: reallocateItem.sales_order_id,
        source_so_number: reallocateItem.so_number,
        source_line_item_id: reallocateItem.line_item_id,
        dest_so_id: selectedDestination.sales_order_id,
        dest_so_number: selectedDestination.so_number,
        dest_line_item_id: selectedDestination.line_item_id,
        item_id: reallocateItem.item_id,
        quantity: reallocateQty,
        reason: reallocateReason
      };
      
      const response = await fetch(`${API_URL}/api/reallocations/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || 'Stock reallocated successfully');
        setShowReallocateDialog(false);
        fetchData(); // Refresh the main data
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to reallocate stock');
      }
    } catch (err) {
      toast.error('Failed to reallocate stock');
    } finally {
      setReallocating(false);
    }
  };

  // Reverse a reallocation
  const handleReverseReallocation = async (reallocationId) => {
    const reason = prompt('Please provide a reason for reversing this reallocation:');
    if (!reason) return;
    
    try {
      const response = await fetch(`${API_URL}/api/reallocations/${reallocationId}/reverse`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({ reason })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || 'Reallocation reversed');
        fetchReallocations();
        fetchData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to reverse reallocation');
      }
    } catch (err) {
      toast.error('Failed to reverse reallocation');
    }
  };

  // ============= DISPATCH PLANS MANAGEMENT (Phase 4) =============
  
  // Fetch all dispatch plans
  const fetchDispatchPlans = async () => {
    setDispatchPlansLoading(true);
    try {
      const params = new URLSearchParams();
      if (dispatchPlansFilter !== 'all') {
        params.append('status', dispatchPlansFilter);
      }
      params.append('limit', '100');
      
      const response = await fetch(`${API_URL}/api/dispatch-plans?${params}`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const plans = await response.json();
        setDispatchPlans(plans);
      }
    } catch (err) {
      console.error('Error fetching dispatch plans:', err);
    } finally {
      setDispatchPlansLoading(false);
    }
  };

  // Valid reset transitions (must match backend)
  const VALID_RESET_TARGETS = {
    'ready_for_dispatch': ['planned'],
    'invoiced': ['ready_for_dispatch', 'planned'],
    'dispatched': ['invoiced', 'ready_for_dispatch'],
    'completed': ['dispatched', 'invoiced']
  };

  // Get allowed reset targets for a plan
  const getResetTargets = (status) => {
    return VALID_RESET_TARGETS[status] || [];
  };

  // Open stage reset dialog
  const handleOpenStageReset = (plan) => {
    const targets = getResetTargets(plan.status);
    if (targets.length === 0) {
      toast.info('This plan cannot be reset (already at earliest stage or cancelled)');
      return;
    }
    setResetPlan(plan);
    setResetTargetStatus(targets[0]); // Default to first option
    setResetReason('');
    setShowStageResetDialog(true);
  };

  // Execute stage reset
  const executeStageReset = async () => {
    if (!resetPlan || !resetTargetStatus || !resetReason.trim()) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setResettingStage(true);
    try {
      const response = await fetch(`${API_URL}/api/dispatch-plans/${resetPlan.id}/reset-stage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({
          target_status: resetTargetStatus,
          reason: resetReason
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || 'Stage reset successful');
        setShowStageResetDialog(false);
        fetchDispatchPlans();
        fetchData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to reset stage');
      }
    } catch (err) {
      toast.error('Failed to reset stage');
    } finally {
      setResettingStage(false);
    }
  };

  // View reset history for a plan
  const viewResetHistory = async (plan) => {
    try {
      const response = await fetch(`${API_URL}/api/dispatch-plans/${plan.id}/reset-history`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setResetHistory(data.reset_history || []);
        setResetPlan(plan);
        setShowResetHistoryDialog(true);
      }
    } catch (err) {
      toast.error('Failed to fetch reset history');
    }
  };

  // ==================== DISPATCH PLAN CRUD HANDLERS ====================
  
  // View dispatch plan details
  const handleViewPlan = async (plan) => {
    setLoadingPlanDetails(true);
    setShowViewPlanDialog(true);
    try {
      const response = await fetch(`${API_URL}/api/dispatch-plans/${plan.id}`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setViewPlanData(data);
      } else {
        toast.error('Failed to load plan details');
        setShowViewPlanDialog(false);
      }
    } catch (err) {
      toast.error('Failed to load plan details');
      setShowViewPlanDialog(false);
    } finally {
      setLoadingPlanDetails(false);
    }
  };

  // Open edit plan dialog
  const handleOpenEditPlan = async (plan) => {
    setLoadingPlanDetails(true);
    setShowEditPlanDialog(true);
    try {
      const response = await fetch(`${API_URL}/api/dispatch-plans/${plan.id}`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setEditPlanData({
          ...data,
          planned_dispatch_date: data.planned_dispatch_date || '',
          shipping_address: data.shipping_address || '',
          remarks: data.remarks || ''
        });
      } else {
        toast.error('Failed to load plan details');
        setShowEditPlanDialog(false);
      }
    } catch (err) {
      toast.error('Failed to load plan details');
      setShowEditPlanDialog(false);
    } finally {
      setLoadingPlanDetails(false);
    }
  };

  // Save edited plan
  const handleSaveEditPlan = async () => {
    if (!editPlanData) return;
    
    setEditingPlan(true);
    try {
      const response = await fetch(`${API_URL}/api/dispatch-plans/${editPlanData.id}`, {
        method: 'PATCH',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          planned_dispatch_date: editPlanData.planned_dispatch_date,
          shipping_address: editPlanData.shipping_address,
          remarks: editPlanData.remarks
        })
      });
      
      if (response.ok) {
        toast.success('Dispatch plan updated successfully');
        setShowEditPlanDialog(false);
        setEditPlanData(null);
        fetchDispatchPlans();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update plan');
      }
    } catch (err) {
      toast.error('Failed to update plan');
    } finally {
      setEditingPlan(false);
    }
  };

  // Open cancel plan dialog
  const handleOpenCancelPlan = (plan) => {
    setCancelPlanData(plan);
    setCancelReason('');
    setShowCancelPlanDialog(true);
  };

  // Confirm cancel plan
  const handleConfirmCancelPlan = async () => {
    if (!cancelPlanData || !cancelReason.trim()) {
      toast.error('Please provide a cancellation reason');
      return;
    }
    
    setCancellingPlan(true);
    try {
      const response = await fetch(`${API_URL}/api/dispatch-plans/${cancelPlanData.id}/cancel`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reason: cancelReason
        })
      });
      
      if (response.ok) {
        toast.success('Dispatch plan cancelled successfully');
        setShowCancelPlanDialog(false);
        setCancelPlanData(null);
        setCancelReason('');
        fetchDispatchPlans();
        fetchData(); // Refresh main data to update ready quantities
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to cancel plan');
      }
    } catch (err) {
      toast.error('Failed to cancel plan');
    } finally {
      setCancellingPlan(false);
    }
  };

  // Get status badge color
  const getStatusColor = (status) => {
    const colors = {
      planned: 'bg-blue-100 text-blue-800',
      ready_for_dispatch: 'bg-yellow-100 text-yellow-800',
      invoiced: 'bg-purple-100 text-purple-800',
      dispatched: 'bg-green-100 text-green-800',
      completed: 'bg-gray-100 text-gray-600',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-600';
  };

  // Ready tab: Handle item selection
  const handleSelectItem = (item, checked) => {
    if (checked) {
      // Check if selecting from different client
      if (selectedItems.length > 0 && selectedItems[0].client_id !== item.client_id) {
        toast.error('Can only select items from one client at a time');
        return;
      }
      setSelectedItems(prev => [...prev, item]);
      setDispatchQuantities(prev => ({
        ...prev,
        [item.line_item_id]: item.ready_quantity
      }));
    } else {
      setSelectedItems(prev => prev.filter(i => i.line_item_id !== item.line_item_id));
      setDispatchQuantities(prev => {
        const updated = { ...prev };
        delete updated[item.line_item_id];
        return updated;
      });
    }
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedItems([]);
    setDispatchQuantities({});
    setIsDirectDispatch(false);
    setSelectedSupplierPO('');
    setSupplierPOs([]);
  };

  // Open dispatch plan dialog
  const openDispatchDialog = () => {
    if (selectedItems.length === 0) {
      toast.error('Please select items first');
      return;
    }
    // Reset direct dispatch state
    setIsDirectDispatch(false);
    setSelectedSupplierPO('');
    setSupplierPOs([]);
    setShowDispatchDialog(true);
  };
  
  // Fetch POs for selected items (for direct dispatch)
  const fetchPOsForItems = async () => {
    if (selectedItems.length === 0) return;
    
    setLoadingPOs(true);
    try {
      // Get unique item IDs from selected items
      const itemIds = [...new Set(selectedItems.map(item => item.item_id))];
      
      // Fetch POs that contain these items
      const response = await fetch(`${API_URL}/api/purchase-orders?status=confirmed,partially_received,received`, {
        headers: getAuthHeader()
      });
      
      if (response.ok) {
        const allPOs = await response.json();
        
        // Filter POs that have line items matching our selected items
        const relevantPOs = allPOs.filter(po => {
          const poItemIds = (po.line_items || []).map(li => li.item_id);
          return itemIds.some(itemId => poItemIds.includes(itemId));
        });
        
        setSupplierPOs(relevantPOs);
      }
    } catch (err) {
      console.error('Error fetching POs:', err);
    } finally {
      setLoadingPOs(false);
    }
  };
  
  // Handle direct dispatch checkbox change
  const handleDirectDispatchChange = (checked) => {
    setIsDirectDispatch(checked);
    if (checked) {
      fetchPOsForItems();
    } else {
      setSelectedSupplierPO('');
      setSupplierPOs([]);
    }
  };

  // Create dispatch plan
  const createDispatchPlan = async () => {
    if (selectedItems.length === 0) return;
    
    if (!selectedLocation) {
      toast.error('Please select a location');
      return;
    }
    
    // Validate direct dispatch requires PO selection
    if (isDirectDispatch && !selectedSupplierPO) {
      toast.error('Please select a supplier PO for direct dispatch');
      return;
    }
    
    setCreatingPlan(true);
    try {
      // Build dispatch plan payload with correct field names matching backend model
      const client = selectedItems[0];
      const items = selectedItems.map(item => ({
        sales_order_id: item.sales_order_id,
        so_number: item.so_number,
        line_item_id: item.line_item_id,
        item_id: item.item_id,
        item_name: item.item_name,
        item_code: item.item_code || '',
        ordered_quantity: item.ordered_quantity,
        already_dispatched_quantity: item.dispatched_quantity || 0,
        planned_quantity: dispatchQuantities[item.line_item_id] || item.ready_quantity,
        unit: 'NOS',
        unit_price: item.unit_price || 0
      }));
      
      const payload = {
        location_id: selectedLocation,
        customer_id: client.client_id,
        customer_name: client.client_name,
        planned_dispatch_date: plannedDate,
        items: items,
        remarks: '',
        // Direct dispatch fields
        is_direct_dispatch: isDirectDispatch,
        supplier_po_id: isDirectDispatch ? selectedSupplierPO : null
      };
      
      const response = await fetch(`${API_URL}/api/dispatch-plans`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to create dispatch plan';
        try {
          const err = JSON.parse(errorText);
          errorMessage = err.detail || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      const result = await response.json();
      const successMsg = isDirectDispatch 
        ? `Direct Dispatch ${result.dispatch_plan_number} completed - SO updated` 
        : `Dispatch Plan ${result.dispatch_plan_number} created successfully`;
      toast.success(successMsg);
      setShowDispatchDialog(false);
      clearSelection();
      
      // Stay on page and refresh data to show updated quantities
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreatingPlan(false);
    }
  };

  // View dispatch details
  const viewDispatchDetails = (dispatch) => {
    setViewDispatch(dispatch);
    setShowViewDialog(true);
  };

  // Calculate selected total
  const selectedTotal = useMemo(() => {
    return selectedItems.reduce((sum, item) => {
      const qty = dispatchQuantities[item.line_item_id] || item.ready_quantity;
      return sum + (qty * (item.unit_price || 0));
    }, 0);
  }, [selectedItems, dispatchQuantities]);

  // Calculate payment info for selected SOs
  const selectedPaymentInfo = useMemo(() => {
    if (selectedItems.length === 0) return { totalSOValue: 0, paymentsReceived: 0, balanceDue: 0 };
    
    // Group by SO to avoid double counting (multiple line items from same SO)
    const soMap = {};
    selectedItems.forEach(item => {
      if (!soMap[item.sales_order_id]) {
        soMap[item.sales_order_id] = {
          so_number: item.so_number,
          total_amount: item.so_total_amount || 0,
          total_advance: item.so_total_advance || 0,
          balance_due: item.so_balance_due || item.so_total_amount || 0
        };
      }
    });
    
    const soList = Object.values(soMap);
    const totalSOValue = soList.reduce((sum, so) => sum + so.total_amount, 0);
    const paymentsReceived = soList.reduce((sum, so) => sum + so.total_advance, 0);
    const balanceDue = soList.reduce((sum, so) => sum + so.balance_due, 0);
    
    return { 
      totalSOValue, 
      paymentsReceived, 
      balanceDue,
      soCount: soList.length,
      soDetails: soList
    };
  }, [selectedItems]);

  // Group items by SO for accordion view
  const groupedBySO = useMemo(() => {
    if (!data.items || data.items.length === 0) return [];
    
    const soMap = {};
    data.items.forEach(item => {
      const soId = item.sales_order_id;
      if (!soMap[soId]) {
        soMap[soId] = {
          so_id: soId,
          so_number: item.so_number,
          client_name: item.client_name,
          client_id: item.client_id,
          delivery_date: item.delivery_date,
          original_delivery_date: item.original_delivery_date,
          date_change_history: item.date_change_history,
          days_to_delivery: item.days_to_delivery,
          is_overdue: item.is_overdue,
          so_total_amount: item.so_total_amount,
          so_total_advance: item.so_total_advance,
          so_balance_due: item.so_balance_due,
          line_items: [],
          total_ordered: 0,
          total_dispatched: 0,
          total_ready: 0,
          total_items: 0
        };
      }
      soMap[soId].line_items.push(item);
      soMap[soId].total_ordered += item.ordered_quantity || 0;
      soMap[soId].total_dispatched += item.dispatched_quantity || 0;
      soMap[soId].total_ready += item.ready_quantity || 0;
      soMap[soId].total_items += 1;
    });
    
    return Object.values(soMap).sort((a, b) => {
      // Sort by delivery date ascending
      const da = a.delivery_date || '9999-12-31';
      const db = b.delivery_date || '9999-12-31';
      return da.localeCompare(db);
    });
  }, [data.items]);

  if (loading && !data.items.length) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center min-h-[400px] text-red-500">
          <AlertCircle className="w-12 h-12 mb-2" />
          <p>Error: {error}</p>
          <Button onClick={fetchData} className="mt-4">Retry</Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="so-fulfillment-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Package className="w-7 h-7" />
              Sales Order Fulfillment
            </h1>
            <p className="text-slate-500 mt-1">Track production, manage dispatch readiness, and view history</p>
          </div>
          <Button onClick={() => { fetchData(); if (activeTab === 'dispatched') fetchDispatches(); }} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        {/* Compact Summary Stats */}
        <div className="flex items-center gap-6 py-2 px-4 bg-gray-50 rounded-lg text-sm">
          <div className="flex items-center gap-1">
            <span className="text-gray-500">Total:</span>
            <span className="font-bold text-lg">{data.summary?.total_so_items || 0}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-blue-500">In Progress:</span>
            <span className="font-bold text-lg text-blue-700">{data.summary?.in_progress || 0}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-green-500">Ready:</span>
            <span className="font-bold text-lg text-green-700">{data.summary?.ready_to_dispatch || 0}</span>
          </div>
          {canSeePrices && (
            <div className="flex items-center gap-1">
              <span className="text-emerald-500">Value:</span>
              <span className="font-bold text-emerald-700">{formatCurrency(data.summary?.total_ready_value)}</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <span className="text-red-500">Overdue:</span>
            <span className="font-bold text-lg text-red-700">{data.summary?.overdue_count || 0}</span>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 items-center flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by SO#, Client, Item..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger className="w-full md:w-56" data-testid="client-filter">
              <SelectValue placeholder="Filter by client" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clients</SelectItem>
              {clients.map(client => (
                <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* F1 — date range (on SO delivery_date, reports.py:706-711) */}
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-40"
              data-testid="sof-from-date"
              placeholder="From"
            />
            <span className="text-xs text-muted-foreground">to</span>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-40"
              data-testid="sof-to-date"
              placeholder="To"
            />
          </div>
          {/* F1 — item filter (client-side on loaded data) */}
          <Select value={itemFilter} onValueChange={setItemFilter}>
            <SelectTrigger className="w-full md:w-56" data-testid="sof-item-filter">
              <SelectValue placeholder="Filter by item" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Items</SelectItem>
              {uniqueItems.map((it) => (
                <SelectItem key={it.id} value={it.id}>
                  {it.item_code ? `${it.item_code} — ${it.item_name}` : it.item_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* F1 — tab-aware Excel export */}
          <Button variant="outline" size="sm" onClick={handleExportClick} data-testid="sof-export-excel-btn">
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(val) => { setActiveTab(val); clearSelection(); setExpandedSO(null); }} className="w-full">
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value="all" data-testid="tab-all">
              All Items ({data.summary?.total_so_items || 0})
            </TabsTrigger>
            <TabsTrigger value="in_progress" data-testid="tab-in-progress">
              In Progress ({data.summary?.in_progress || 0})
            </TabsTrigger>
            <TabsTrigger value="ready" data-testid="tab-ready">
              <span className="flex items-center gap-1">
                Ready to Dispatch
                {(data.summary?.ready_to_dispatch || 0) > 0 && (
                  <Badge className="bg-green-500 text-white ml-1">{data.summary?.ready_to_dispatch}</Badge>
                )}
              </span>
            </TabsTrigger>
            <TabsTrigger value="dispatched" data-testid="tab-dispatched">
              Dispatched
            </TabsTrigger>
            <TabsTrigger value="plans" data-testid="tab-plans">
              <span className="flex items-center gap-1">
                <ClipboardList className="w-3 h-3" />
                Plans
              </span>
            </TabsTrigger>
            <TabsTrigger value="reallocations" data-testid="tab-reallocations">
              <span className="flex items-center gap-1">
                <ArrowRightLeft className="w-3 h-3" />
                Reallocations
              </span>
            </TabsTrigger>
            <TabsTrigger value="line_items" data-testid="tab-line-items">
              <span className="flex items-center gap-1">
                <Package className="w-3 h-3" />
                Line Items
              </span>
            </TabsTrigger>
          </TabsList>

          {/* All Items Tab */}
          <TabsContent value="all" className="mt-4">
            <Card>
              <CardContent className="p-0">
                {/* Header for SO-grouped view */}
                <div className="flex items-center py-2 px-4 bg-gray-100 text-xs font-semibold text-gray-600 uppercase border-b">
                  <div className="w-6"></div>
                  <div className="w-40">SO / Client</div>
                  <div className="flex-1">Readiness</div>
                  <div className="w-16 text-center">Items</div>
                  <div className="w-24 text-center">Delivery</div>
                </div>
                
                {groupedBySO.length === 0 ? (
                  <div className="py-12 text-center text-gray-500">
                    <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No items found</p>
                  </div>
                ) : (
                  groupedBySO.map(soGroup => (
                    <SOAccordionRow
                      key={soGroup.so_id}
                      soGroup={soGroup}
                      isExpanded={expandedSO === soGroup.so_id}
                      onToggle={() => setExpandedSO(prev => prev === soGroup.so_id ? null : soGroup.so_id)}
                      onReallocate={handleReallocate}
                      canTakeActions={canTakeActions}
                      canSeePrices={canSeePrices}
                    />
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* In Progress Tab */}
          <TabsContent value="in_progress" className="mt-4">
            <Card>
              <CardContent className="p-0">
                {/* Header for SO-grouped view */}
                <div className="flex items-center py-2 px-4 bg-gray-100 text-xs font-semibold text-gray-600 uppercase border-b">
                  <div className="w-6"></div>
                  <div className="w-40">SO / Client</div>
                  <div className="flex-1">Readiness</div>
                  <div className="w-16 text-center">Items</div>
                  <div className="w-24 text-center">Delivery</div>
                </div>
                
                {groupedBySO.length === 0 ? (
                  <div className="py-12 text-center text-gray-500">
                    <Loader2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No items in progress</p>
                  </div>
                ) : (
                  groupedBySO.map(soGroup => (
                    <SOAccordionRow
                      key={soGroup.so_id}
                      soGroup={soGroup}
                      isExpanded={expandedSO === soGroup.so_id}
                      onToggle={() => setExpandedSO(prev => prev === soGroup.so_id ? null : soGroup.so_id)}
                      onReallocate={handleReallocate}
                      canTakeActions={canTakeActions}
                      canSeePrices={canSeePrices}
                    />
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Ready to Dispatch Tab */}
          <TabsContent value="ready" className="mt-4">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Truck className="w-5 h-5" />
                    Items Ready for Dispatch
                  </CardTitle>
                  {canTakeActions && selectedItems.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">{selectedItems.length} items selected</span>
                      <Button variant="outline" size="sm" onClick={clearSelection}>Clear</Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {canTakeActions && <TableHead className="w-10"></TableHead>}
                      <TableHead>SO / Client</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-center">Type</TableHead>
                      <TableHead className="text-center">Ordered</TableHead>
                      <TableHead className="text-center">Dispatched</TableHead>
                      <TableHead className="text-center">In Plans</TableHead>
                      <TableHead className="text-center">Ready Qty</TableHead>
                      {canSeePrices && <TableHead className="text-center">Ready Value</TableHead>}
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead className="text-center">Delivery</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={canTakeActions ? 11 : 10} className="text-center py-12 text-gray-500">
                          <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p>No items ready for dispatch</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      [...data.items].sort((a, b) => {
                        const da = a.delivery_date || '9999-12-31';
                        const db = b.delivery_date || '9999-12-31';
                        return da.localeCompare(db);
                      }).map(item => (
                        <ReadyItemRow
                          key={item.line_item_id}
                          item={item}
                          isSelected={selectedItems.some(i => i.line_item_id === item.line_item_id)}
                          onSelect={handleSelectItem}
                          disabled={selectedItems.length > 0 && selectedItems[0].client_id !== item.client_id}
                          canSeePrices={canSeePrices}
                          canTakeActions={canTakeActions}
                        />
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Floating Action Bar - Only show if user can take actions */}
            {canTakeActions && selectedItems.length > 0 && (
              <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-primary text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-4 z-50">
                <span className="font-medium">{selectedItems.length} items selected</span>
                {canSeePrices && <span className="text-sm opacity-80">({formatCurrency(selectedTotal)})</span>}
                <Button onClick={openDispatchDialog} variant="secondary" className="gap-2" data-testid="create-dispatch-plan-btn">
                  <Truck className="w-4 h-4" />
                  Create Dispatch Plan
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Dispatched Tab */}
          <TabsContent value="dispatched" className="mt-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Dispatch History
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Dispatch #</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>SO #</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Invoice #</TableHead>
                      {canSeePrices && <TableHead className="text-right">Amount</TableHead>}
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dispatches.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={canSeePrices ? 7 : 6} className="text-center py-12 text-gray-500">
                          <Truck className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p>No dispatches found</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      dispatches.map(dispatch => (
                        <DispatchRow
                          key={dispatch.id || dispatch.dispatch_number}
                          dispatch={dispatch}
                          onView={viewDispatchDetails}
                          canSeePrices={canSeePrices}
                        />
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Dispatch Plans Tab (Phase 4 - Stage Reset) */}
          <TabsContent value="plans" className="mt-4">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ClipboardList className="w-5 h-5" />
                    Dispatch Plans Management
                  </CardTitle>
                  <div className="flex items-center gap-4">
                    <Select value={dispatchPlansFilter} onValueChange={setDispatchPlansFilter}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Filter by status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        <SelectItem value="planned">Planned</SelectItem>
                        <SelectItem value="ready_for_dispatch">Ready for Dispatch</SelectItem>
                        <SelectItem value="invoiced">Invoiced</SelectItem>
                        <SelectItem value="dispatched">Dispatched</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={fetchDispatchPlans}>
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Refresh
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Plan #</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead className="text-center">Items</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Vehicle</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dispatchPlansLoading ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-12">
                          <Loader2 className="w-8 h-8 mx-auto animate-spin text-gray-400" />
                          <p className="text-gray-500 mt-2">Loading dispatch plans...</p>
                        </TableCell>
                      </TableRow>
                    ) : dispatchPlans.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-12 text-gray-500">
                          <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p>No dispatch plans found</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      dispatchPlans.map(plan => (
                        <TableRow key={plan.id}>
                          <TableCell className="font-medium">{plan.dispatch_plan_number}</TableCell>
                          <TableCell className="text-sm">{plan.planned_dispatch_date}</TableCell>
                          <TableCell>
                            <div className="font-medium">{plan.customer_name}</div>
                          </TableCell>
                          <TableCell className="text-center">{plan.items?.length || 0}</TableCell>
                          <TableCell className="text-right font-medium">
                            {plan.total_planned_quantity}
                          </TableCell>
                          <TableCell>
                            <Badge className={getStatusColor(plan.status)}>
                              {plan.status_label || plan.status?.replace(/_/g, ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">{plan.vehicle_number || '-'}</TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              {/* View Details */}
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleViewPlan(plan)}
                                title="View details"
                                data-testid={`view-plan-${plan.id}`}
                              >
                                <Eye className="w-4 h-4 text-blue-600" />
                              </Button>
                              
                              {/* Edit - only for planned status and if user can take actions */}
                              {canTakeActions && plan.status === 'planned' && (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleOpenEditPlan(plan)}
                                  title="Edit plan"
                                  data-testid={`edit-plan-${plan.id}`}
                                >
                                  <Pencil className="w-4 h-4 text-amber-600" />
                                </Button>
                              )}
                              
                              {/* Cancel - only for planned status and if user can take actions */}
                              {canTakeActions && plan.status === 'planned' && (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleOpenCancelPlan(plan)}
                                  title="Cancel plan"
                                  data-testid={`cancel-plan-${plan.id}`}
                                >
                                  <XCircle className="w-4 h-4 text-red-600" />
                                </Button>
                              )}
                              
                              {/* Reset Stage - only if user can take actions */}
                              {canTakeActions && getResetTargets(plan.status).length > 0 && (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleOpenStageReset(plan)}
                                  title="Reset to previous stage"
                                  data-testid={`reset-${plan.id}`}
                                >
                                  <RotateCcw className="w-4 h-4 text-orange-600" />
                                </Button>
                              )}
                              
                              {/* Reset History - visible for all users */}
                              {(plan.reset_history?.length > 0) && (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => viewResetHistory(plan)}
                                  title="View reset history"
                                >
                                  <History className="w-4 h-4 text-gray-500" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reallocations Tab */}
          <TabsContent value="reallocations" className="mt-4">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ArrowRightLeft className="w-5 h-5" />
                    Stock Reallocations
                  </CardTitle>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-gray-500">
                      Total: <strong>{reallocationsSummary.total_reallocations}</strong>
                    </span>
                    <span className="text-green-600">
                      Active: <strong>{reallocationsSummary.completed?.count || 0}</strong>
                    </span>
                    <span className="text-orange-600">
                      Reversed: <strong>{reallocationsSummary.reversed?.count || 0}</strong>
                    </span>
                    <Button variant="outline" size="sm" onClick={fetchReallocations}>
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Refresh
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead>From SO</TableHead>
                      <TableHead>To SO</TableHead>
                      <TableHead className="text-center">Qty</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reallocationsLoading ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-12">
                          <Loader2 className="w-8 h-8 mx-auto animate-spin text-gray-400" />
                          <p className="text-gray-500 mt-2">Loading reallocations...</p>
                        </TableCell>
                      </TableRow>
                    ) : reallocations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-12 text-gray-500">
                          <ArrowRightLeft className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p>No reallocations yet</p>
                          <p className="text-sm mt-1">Use the "Reallocate Stock" action from the All Items tab</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      reallocations.map(realloc => (
                        <TableRow key={realloc.id} className={realloc.status === 'reversed' ? 'bg-gray-50 opacity-60' : ''}>
                          <TableCell className="text-sm">
                            {formatDate(realloc.created_at)}
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{realloc.item_name}</div>
                            {realloc.item_code && <div className="text-xs text-gray-500">{realloc.item_code}</div>}
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{realloc.source_so_number}</div>
                            <div className="text-xs text-gray-500">{realloc.source_customer_name}</div>
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{realloc.dest_so_number}</div>
                            <div className="text-xs text-gray-500">{realloc.dest_customer_name}</div>
                          </TableCell>
                          <TableCell className="text-center font-bold">
                            {realloc.quantity} {realloc.unit}
                          </TableCell>
                          <TableCell className="max-w-xs truncate" title={realloc.reason}>
                            {realloc.reason}
                          </TableCell>
                          <TableCell className="text-center">
                            {realloc.status === 'completed' ? (
                              <Badge className="bg-green-100 text-green-800">Active</Badge>
                            ) : (
                              <Badge variant="outline" className="text-gray-500">Reversed</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {canTakeActions && realloc.status === 'completed' && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleReverseReallocation(realloc.id)}
                                title="Reverse this reallocation"
                              >
                                <Undo2 className="w-4 h-4" />
                              </Button>
                            )}
                            {realloc.status === 'reversed' && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <span className="text-xs text-gray-500">
                                      by {realloc.reversed_by_name}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Reversed on {formatDate(realloc.reversed_at)}</p>
                                    <p>Reason: {realloc.reversal_reason}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* F1 — Line Items Tab (read-only flat view of every line item) */}
          <TabsContent value="line_items" className="mt-4" data-testid="tab-line-items-content">
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>SO Number</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Item Code</TableHead>
                        <TableHead>Item Name</TableHead>
                        <TableHead className="text-right">Ordered</TableHead>
                        <TableHead className="text-right">Dispatched</TableHead>
                        <TableHead className="text-right">Pending</TableHead>
                        <TableHead>Target Production</TableHead>
                        <TableHead>Production Status</TableHead>
                        <TableHead>WO Numbers</TableHead>
                        <TableHead>Indent Numbers</TableHead>
                        <TableHead>Last Dispatch</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(() => {
                        const rows = [...filteredItems].sort((a, b) => {
                          const ta = a.target_production_date || '9999-12-31';
                          const tb = b.target_production_date || '9999-12-31';
                          if (ta !== tb) return ta.localeCompare(tb);
                          const sa = a.so_number || '';
                          const sb = b.so_number || '';
                          return sa.localeCompare(sb);
                        });
                        if (rows.length === 0) {
                          return (
                            <TableRow>
                              <TableCell colSpan={12} className="text-center text-muted-foreground py-10">
                                No line items match the current filters.
                              </TableCell>
                            </TableRow>
                          );
                        }
                        return rows.map((r) => {
                          const ps = computeProductionStatus(r);
                          const ordered = r.ordered_quantity || 0;
                          const dispatched = r.dispatched_quantity || 0;
                          const pending = Math.max(0, ordered - dispatched);
                          return (
                            <TableRow key={`${r.sales_order_id}::${r.line_item_id}`} data-testid={`line-items-row-${r.line_item_id}`}>
                              <TableCell className="font-medium">{r.so_number}</TableCell>
                              <TableCell>{r.client_name}</TableCell>
                              <TableCell>{r.item_code || '—'}</TableCell>
                              <TableCell>{r.item_name}</TableCell>
                              <TableCell className="text-right font-mono">{ordered}</TableCell>
                              <TableCell className="text-right font-mono">{dispatched}</TableCell>
                              <TableCell className="text-right font-mono">{pending}</TableCell>
                              <TableCell>
                                {r.target_production_date
                                  ? format(new Date(r.target_production_date), 'd MMM yyyy')
                                  : <span className="text-muted-foreground">—</span>}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className={ps.tone}>{ps.label}</Badge>
                              </TableCell>
                              <TableCell className="text-xs">
                                {(r.wo_numbers || []).length > 0
                                  ? (r.wo_numbers || []).join(', ')
                                  : <span className="text-muted-foreground">—</span>}
                              </TableCell>
                              <TableCell className="text-xs">
                                {(r.indent_numbers || []).length > 0
                                  ? (r.indent_numbers || []).join(', ')
                                  : <span className="text-muted-foreground">—</span>}
                              </TableCell>
                              <TableCell className="text-xs">
                                {r.last_dispatch_date
                                  ? format(new Date(r.last_dispatch_date), 'd MMM yyyy')
                                  : <span className="text-muted-foreground">—</span>}
                              </TableCell>
                            </TableRow>
                          );
                        });
                      })()}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Dispatch Plan Dialog */}
      <Dialog open={showDispatchDialog} onOpenChange={setShowDispatchDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5" />
              Create Dispatch Plan
            </DialogTitle>
          </DialogHeader>
          
          {selectedItems.length > 0 && (
            <div className="space-y-4">
              {/* Client Info */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Client</p>
                <p className="font-bold text-lg">{selectedItems[0].client_name}</p>
              </div>
              
              {/* Location Selection */}
              <div>
                <label className="text-sm font-medium">Dispatch Location</label>
                <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map(loc => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Plan Date */}
              <div>
                <label className="text-sm font-medium">Planned Dispatch Date</label>
                <Input 
                  type="date" 
                  value={plannedDate}
                  onChange={(e) => setPlannedDate(e.target.value)}
                  className="mt-1"
                />
              </div>
              
              {/* Direct Dispatch Option */}
              <div className="p-3 border rounded-lg bg-amber-50 border-amber-200">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="direct-dispatch"
                    checked={isDirectDispatch}
                    onCheckedChange={handleDirectDispatchChange}
                  />
                  <label htmlFor="direct-dispatch" className="text-sm font-medium cursor-pointer">
                    Direct dispatch from supplier (goods not received in warehouse)
                  </label>
                </div>
                
                {isDirectDispatch && (
                  <div className="mt-3 space-y-2">
                    <label className="text-sm font-medium">Supplier PO *</label>
                    {loadingPOs ? (
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading POs...
                      </div>
                    ) : (
                      <Select value={selectedSupplierPO} onValueChange={setSelectedSupplierPO}>
                        <SelectTrigger className="bg-white">
                          <SelectValue placeholder="Select supplier PO" />
                        </SelectTrigger>
                        <SelectContent>
                          {supplierPOs.length === 0 ? (
                            <SelectItem value="_none" disabled>No matching POs found</SelectItem>
                          ) : (
                            supplierPOs.map(po => (
                              <SelectItem key={po.id} value={po.id}>
                                {po.po_number} - {po.supplier_name}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    )}
                    <p className="text-xs text-amber-700">
                      Direct dispatch bypasses warehouse - goods ship directly from supplier to customer.
                      Stock will not be decremented. Cost will be taken from PO for GP reports.
                    </p>
                  </div>
                )}
              </div>
              
              {/* Items Table */}
              <div>
                <label className="text-sm font-medium mb-2 block">Items to Dispatch</label>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>SO #</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-center">{isDirectDispatch ? 'Qty' : 'Available'}</TableHead>
                      <TableHead className="text-center">Plan Qty</TableHead>
                      {canSeePrices && <TableHead className="text-right">Value</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedItems.map(item => (
                      <TableRow key={item.line_item_id}>
                        <TableCell className="font-medium">{item.so_number}</TableCell>
                        <TableCell>
                          <div>{item.item_name}</div>
                          <div className="text-xs text-gray-500">{item.item_code}</div>
                        </TableCell>
                        <TableCell className={`text-center ${isDirectDispatch ? 'text-gray-600' : 'text-green-600'} font-medium`}>
                          {isDirectDispatch ? (item.ordered_quantity - (item.dispatched_quantity || 0)) : item.ready_quantity}
                        </TableCell>
                        <TableCell className="text-center">
                          <Input
                            type="number"
                            min={1}
                            max={isDirectDispatch ? (item.ordered_quantity - (item.dispatched_quantity || 0)) : item.ready_quantity}
                            value={dispatchQuantities[item.line_item_id] || (isDirectDispatch ? (item.ordered_quantity - (item.dispatched_quantity || 0)) : item.ready_quantity)}
                            onChange={(e) => {
                              const maxQty = isDirectDispatch ? (item.ordered_quantity - (item.dispatched_quantity || 0)) : item.ready_quantity;
                              setDispatchQuantities(prev => ({
                                ...prev,
                                [item.line_item_id]: Math.min(Number(e.target.value), maxQty)
                              }));
                            }}
                            className="w-20 text-center"
                          />
                        </TableCell>
                        {canSeePrices && (
                          <TableCell className="text-right">
                            {formatCurrency((dispatchQuantities[item.line_item_id] || (isDirectDispatch ? (item.ordered_quantity - (item.dispatched_quantity || 0)) : item.ready_quantity)) * (item.unit_price || 0))}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Totals - Only show for users who can see prices */}
              {canSeePrices && (
              <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                {/* Dispatch Value */}
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Dispatch Value (this plan)</span>
                  <span className="text-xl font-bold">{formatCurrency(selectedTotal)}</span>
                </div>
                
                {/* Payment Info */}
                <div className="border-t pt-3 space-y-2">
                  <p className="text-xs text-gray-500 font-medium uppercase">Payment Status for Selected SO(s)</p>
                  
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">SO Total</p>
                      <p className="font-semibold">{formatCurrency(selectedPaymentInfo.totalSOValue)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Received</p>
                      <p className="font-semibold text-green-600">{formatCurrency(selectedPaymentInfo.paymentsReceived)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Balance Due</p>
                      <p className={`font-semibold ${selectedPaymentInfo.balanceDue > 0 ? 'text-orange-600' : 'text-gray-600'}`}>
                        {formatCurrency(selectedPaymentInfo.balanceDue)}
                      </p>
                    </div>
                  </div>
                  
                  {/* Per-SO breakdown if multiple SOs */}
                  {selectedPaymentInfo.soCount > 1 && (
                    <div className="mt-2 pt-2 border-t border-dashed">
                      <p className="text-xs text-gray-400 mb-1">Breakdown by SO (Advance Rcvd / SO Total):</p>
                      <div className="space-y-1 text-xs">
                        {selectedPaymentInfo.soDetails?.map((so, idx) => (
                          <div key={idx} className="flex justify-between">
                            <span className="font-mono">{so.so_number}</span>
                            <span>
                              <span className="text-green-600">{formatCurrency(so.total_advance)}</span>
                              <span className="text-gray-400"> / </span>
                              <span>{formatCurrency(so.total_amount)}</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDispatchDialog(false)}>Cancel</Button>
            <Button onClick={createDispatchPlan} disabled={creatingPlan} className="gap-2">
              {creatingPlan && <Loader2 className="w-4 h-4 animate-spin" />}
              {isDirectDispatch ? 'Complete Direct Dispatch' : 'Create Plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dispatch Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Dispatch Details
            </DialogTitle>
          </DialogHeader>
          
          {viewDispatch && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Dispatch Number</p>
                  <p className="font-mono font-bold">{viewDispatch.dispatch_number}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Date</p>
                  <p className="font-medium">{formatDate(viewDispatch.dispatch_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Sales Order</p>
                  <p className="font-medium">{viewDispatch.so_number}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Client</p>
                  <p className="font-medium">{viewDispatch.customer_name || viewDispatch.client_name}</p>
                </div>
                {viewDispatch.invoice_number && (
                  <div>
                    <p className="text-sm text-gray-500">Invoice/DC No.</p>
                    <p className="font-medium">{viewDispatch.invoice_number}</p>
                  </div>
                )}
                {viewDispatch.lr_number && (
                  <div>
                    <p className="text-sm text-gray-500">LR Number</p>
                    <p className="font-medium">{viewDispatch.lr_number}</p>
                  </div>
                )}
              </div>
              
              {/* Cost Summary - Only for users who can see prices */}
              {canSeePrices && (viewDispatch.purchase_amount || viewDispatch.production_cost) && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-600 font-medium">Purchase Amount</p>
                    <p className="text-2xl font-bold text-blue-700">{formatCurrency(viewDispatch.purchase_amount)}</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-green-600 font-medium">Production Cost</p>
                    <p className="text-2xl font-bold text-green-700">{formatCurrency(viewDispatch.production_cost)}</p>
                  </div>
                </div>
              )}
              
              {viewDispatch.line_items && viewDispatch.line_items.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Dispatched Items</p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead className="text-right">Ordered</TableHead>
                        <TableHead className="text-right">Dispatched</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {viewDispatch.line_items.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{item.item_name}</TableCell>
                          <TableCell className="text-right">{item.ordered_quantity}</TableCell>
                          <TableCell className="text-right text-green-600 font-medium">{item.dispatch_quantity}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              
              {viewDispatch.remarks && (
                <div className="p-3 bg-yellow-50 rounded-lg">
                  <p className="text-sm text-gray-500">Remarks</p>
                  <p>{viewDispatch.remarks}</p>
                </div>
              )}
              
              <div className="text-xs text-gray-500">
                Created by {viewDispatch.created_by_name} on {formatDate(viewDispatch.created_at)}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reallocation Dialog */}
      <Dialog open={showReallocateDialog} onOpenChange={setShowReallocateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5" />
              Reallocate Stock
            </DialogTitle>
          </DialogHeader>
          
          {reallocateItem && (
            <div className="space-y-4">
              {/* Source Info */}
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-sm text-orange-600 font-medium mb-2">Moving FROM</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-gray-500">Sales Order</p>
                    <p className="font-medium">{reallocateItem.so_number}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Client</p>
                    <p className="font-medium">{reallocateItem.client_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Item</p>
                    <p className="font-medium">{reallocateItem.item_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Ready Quantity</p>
                    <p className="font-bold text-green-600">{reallocateItem.ready_quantity} {reallocateItem.unit || 'NOS'}</p>
                  </div>
                </div>
              </div>
              
              {/* Destination Selection */}
              <div>
                <Label className="text-sm font-medium">Move TO (Select Destination SO)</Label>
                <div className="mt-2 max-h-48 overflow-y-auto border rounded-lg">
                  {loadingDestinations ? (
                    <div className="p-4 text-center">
                      <Loader2 className="w-6 h-6 mx-auto animate-spin text-gray-400" />
                      <p className="text-sm text-gray-500 mt-2">Finding eligible destinations...</p>
                    </div>
                  ) : eligibleDestinations.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No other Sales Orders found for this item</p>
                    </div>
                  ) : (
                    eligibleDestinations.map(dest => (
                      <div
                        key={`${dest.sales_order_id}-${dest.line_item_id}`}
                        className={`p-3 border-b last:border-0 cursor-pointer hover:bg-blue-50 transition-colors ${
                          selectedDestination?.line_item_id === dest.line_item_id ? 'bg-blue-100 border-blue-300' : ''
                        }`}
                        onClick={() => setSelectedDestination(dest)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{dest.so_number}</p>
                            <p className="text-xs text-gray-500">{dest.customer_name}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm">Ordered: {dest.ordered_qty}</p>
                            <p className="text-xs text-gray-500">Available: {dest.available_qty}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              
              {/* Quantity */}
              <div>
                <Label className="text-sm font-medium">Quantity to Reallocate</Label>
                <Input
                  type="number"
                  min={1}
                  max={reallocateItem.ready_quantity}
                  value={reallocateQty}
                  onChange={(e) => setReallocateQty(Math.min(Number(e.target.value), reallocateItem.ready_quantity))}
                  className="mt-1 w-32"
                />
                <p className="text-xs text-gray-500 mt-1">Max: {reallocateItem.ready_quantity}</p>
              </div>
              
              {/* Reason */}
              <div>
                <Label className="text-sm font-medium">Reason for Reallocation *</Label>
                <Textarea
                  value={reallocateReason}
                  onChange={(e) => setReallocateReason(e.target.value)}
                  placeholder="e.g., Customer B has urgent requirement, Customer A delivery postponed..."
                  className="mt-1"
                  rows={2}
                />
              </div>
              
              {/* Summary */}
              {selectedDestination && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm">
                    <strong>{reallocateQty}</strong> units of <strong>{reallocateItem.item_name}</strong> will be moved from{' '}
                    <strong>{reallocateItem.so_number}</strong> to <strong>{selectedDestination.so_number}</strong>
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReallocateDialog(false)}>Cancel</Button>
            <Button 
              onClick={executeReallocation} 
              disabled={reallocating || !selectedDestination || !reallocateQty || !reallocateReason.trim()}
              className="gap-2"
            >
              {reallocating && <Loader2 className="w-4 h-4 animate-spin" />}
              <ArrowRightLeft className="w-4 h-4" />
              Reallocate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stage Reset Dialog */}
      <Dialog open={showStageResetDialog} onOpenChange={setShowStageResetDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <RotateCcw className="w-5 h-5" />
              Reset Dispatch Plan Stage
            </DialogTitle>
          </DialogHeader>
          
          {resetPlan && (
            <div className="space-y-4">
              {/* Plan Info */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">Plan:</span> <strong>{resetPlan.dispatch_plan_number}</strong></div>
                  <div><span className="text-gray-500">Current Status:</span> <Badge className={getStatusColor(resetPlan.status)}>{resetPlan.status?.replace(/_/g, ' ')}</Badge></div>
                  <div><span className="text-gray-500">Customer:</span> {resetPlan.customer_name}</div>
                  <div><span className="text-gray-500">Date:</span> {resetPlan.planned_dispatch_date}</div>
                </div>
              </div>
              
              {/* Target Status */}
              <div>
                <Label className="text-sm font-medium">Reset To *</Label>
                <Select value={resetTargetStatus} onValueChange={setResetTargetStatus}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select target status" />
                  </SelectTrigger>
                  <SelectContent>
                    {getResetTargets(resetPlan.status).map(status => (
                      <SelectItem key={status} value={status}>
                        {status.replace(/_/g, ' ').toUpperCase()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Reason */}
              <div>
                <Label className="text-sm font-medium">Reason for Reset *</Label>
                <Textarea
                  value={resetReason}
                  onChange={(e) => setResetReason(e.target.value)}
                  placeholder="Why is this reset needed? e.g., Invoice had wrong details, Need to reload..."
                  className="mt-1"
                  rows={3}
                />
              </div>
              
              {/* Warning */}
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-800 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Warning</p>
                  <p>Resetting to an earlier stage may clear certain data (vehicle, invoice, gate pass info) depending on the target stage.</p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStageResetDialog(false)}>Cancel</Button>
            <Button 
              onClick={executeStageReset} 
              disabled={resettingStage || !resetTargetStatus || !resetReason.trim()}
              className="gap-2 bg-orange-600 hover:bg-orange-700"
            >
              {resettingStage && <Loader2 className="w-4 h-4 animate-spin" />}
              <RotateCcw className="w-4 h-4" />
              Reset Stage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset History Dialog */}
      <Dialog open={showResetHistoryDialog} onOpenChange={setShowResetHistoryDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Reset History - {resetPlan?.dispatch_plan_number}
            </DialogTitle>
          </DialogHeader>
          
          <div className="max-h-80 overflow-y-auto">
            {resetHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No reset history found
              </div>
            ) : (
              <div className="space-y-3">
                {resetHistory.map((entry, idx) => (
                  <div key={idx} className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{entry.from_status?.replace(/_/g, ' ')}</Badge>
                        <ArrowRight className="w-4 h-4 text-gray-400" />
                        <Badge className={getStatusColor(entry.to_status)}>{entry.to_status?.replace(/_/g, ' ')}</Badge>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">{entry.reason}</p>
                    <p className="text-xs text-gray-400 mt-2">
                      by {entry.reset_by_name} • {formatDate(entry.reset_at)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetHistoryDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dispatch Plan Dialog */}
      <Dialog open={showViewPlanDialog} onOpenChange={setShowViewPlanDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              Dispatch Plan Details
            </DialogTitle>
          </DialogHeader>
          
          {loadingPlanDetails ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : viewPlanData ? (
            <div className="space-y-4">
              {/* Plan Header Info */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-xs text-gray-500">Plan Number</p>
                  <p className="font-semibold">{viewPlanData.dispatch_plan_number}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <Badge className={getStatusColor(viewPlanData.status)}>
                    {viewPlanData.status?.replace(/_/g, ' ')}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Planned Date</p>
                  <p className="font-medium">{viewPlanData.planned_dispatch_date}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Customer</p>
                  <p className="font-medium">{viewPlanData.customer_name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Total Quantity</p>
                  <p className="font-medium">{viewPlanData.total_planned_quantity}</p>
                </div>
                {canSeePrices && (
                <div>
                  <p className="text-xs text-gray-500">Total Value</p>
                  <p className="font-medium">{formatCurrency(viewPlanData.total_value)}</p>
                </div>
                )}
              </div>
              
              {/* Shipping & Vehicle Info */}
              {(viewPlanData.shipping_address || viewPlanData.vehicle_number) && (
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                    <Truck className="w-4 h-4" />
                    Shipping Details
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {viewPlanData.shipping_address && (
                      <div>
                        <p className="text-xs text-gray-500">Shipping Address</p>
                        <p className="flex items-start gap-1">
                          <MapPin className="w-3 h-3 mt-1 flex-shrink-0" />
                          {viewPlanData.shipping_address}
                        </p>
                      </div>
                    )}
                    {viewPlanData.vehicle_number && (
                      <div>
                        <p className="text-xs text-gray-500">Vehicle Number</p>
                        <p>{viewPlanData.vehicle_number}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Invoice Info - Only show amount for users who can see prices */}
              {viewPlanData.invoice_number && (
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Invoice Details
                  </h4>
                  <div className={`grid ${canSeePrices ? 'grid-cols-3' : 'grid-cols-2'} gap-4 text-sm`}>
                    <div>
                      <p className="text-xs text-gray-500">Invoice Number</p>
                      <p className="font-mono">{viewPlanData.invoice_number}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Invoice Date</p>
                      <p>{viewPlanData.invoice_date}</p>
                    </div>
                    {canSeePrices && (
                    <div>
                      <p className="text-xs text-gray-500">Invoice Amount</p>
                      <p>{formatCurrency(viewPlanData.invoice_amount)}</p>
                    </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Items Table */}
              <div>
                <h4 className="font-medium text-sm mb-2">Items ({viewPlanData.items?.length || 0})</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>SO #</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Planned Qty</TableHead>
                      <TableHead className="text-right">Actual Qty</TableHead>
                      {canSeePrices && <TableHead className="text-right">Unit Price</TableHead>}
                      {canSeePrices && <TableHead className="text-right">Value</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {viewPlanData.items?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-mono text-sm">{item.so_number}</TableCell>
                        <TableCell>
                          <div>{item.item_name}</div>
                          {item.item_code && (
                            <div className="text-xs text-blue-600 font-mono">{item.item_code}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">{item.planned_quantity}</TableCell>
                        <TableCell className="text-right">{item.actual_quantity || '-'}</TableCell>
                        {canSeePrices && <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>}
                        {canSeePrices && <TableCell className="text-right">{formatCurrency(item.line_total)}</TableCell>}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Remarks */}
              {viewPlanData.remarks && (
                <div className="p-3 bg-yellow-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Remarks</p>
                  <p className="text-sm">{viewPlanData.remarks}</p>
                </div>
              )}
              
              {/* Created Info */}
              <div className="text-xs text-gray-400 pt-2 border-t">
                Created by {viewPlanData.created_by_name} on {formatDate(viewPlanData.created_at)}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">Failed to load plan details</div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewPlanDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dispatch Plan Dialog */}
      <Dialog open={showEditPlanDialog} onOpenChange={setShowEditPlanDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5" />
              Edit Dispatch Plan
            </DialogTitle>
          </DialogHeader>
          
          {loadingPlanDetails ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : editPlanData ? (
            <div className="space-y-4">
              {/* Plan Info */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">Plan:</span> <strong>{editPlanData.dispatch_plan_number}</strong></div>
                  <div><span className="text-gray-500">Customer:</span> {editPlanData.customer_name}</div>
                </div>
              </div>
              
              {/* Editable Fields */}
              <div>
                <Label className="text-sm font-medium">Planned Dispatch Date</Label>
                <Input
                  type="date"
                  value={editPlanData.planned_dispatch_date || ''}
                  onChange={(e) => setEditPlanData(prev => ({ ...prev, planned_dispatch_date: e.target.value }))}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium">Shipping Address</Label>
                <Textarea
                  value={editPlanData.shipping_address || ''}
                  onChange={(e) => setEditPlanData(prev => ({ ...prev, shipping_address: e.target.value }))}
                  placeholder="Enter shipping address..."
                  className="mt-1"
                  rows={3}
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium">Remarks</Label>
                <Textarea
                  value={editPlanData.remarks || ''}
                  onChange={(e) => setEditPlanData(prev => ({ ...prev, remarks: e.target.value }))}
                  placeholder="Any additional notes..."
                  className="mt-1"
                  rows={2}
                />
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">Failed to load plan details</div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditPlanDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleSaveEditPlan} 
              disabled={editingPlan || !editPlanData}
              className="gap-2"
            >
              {editingPlan && <Loader2 className="w-4 h-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dispatch Plan Dialog */}
      <Dialog open={showCancelPlanDialog} onOpenChange={setShowCancelPlanDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              Cancel Dispatch Plan
            </DialogTitle>
          </DialogHeader>
          
          {cancelPlanData && (
            <div className="space-y-4">
              {/* Plan Info */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">Plan:</span> <strong>{cancelPlanData.dispatch_plan_number}</strong></div>
                  <div><span className="text-gray-500">Customer:</span> {cancelPlanData.customer_name}</div>
                  <div><span className="text-gray-500">Items:</span> {cancelPlanData.items?.length || 0}</div>
                  <div><span className="text-gray-500">Qty:</span> {cancelPlanData.total_planned_quantity}</div>
                </div>
              </div>
              
              {/* Cancellation Reason */}
              <div>
                <Label className="text-sm font-medium">Cancellation Reason *</Label>
                <Textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Why is this plan being cancelled? e.g., Customer cancelled order, Incorrect items..."
                  className="mt-1"
                  rows={3}
                />
              </div>
              
              {/* Warning */}
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Warning</p>
                  <p>Cancelling this plan will release the reserved quantities back to the available stock. This action cannot be undone.</p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelPlanDialog(false)}>Go Back</Button>
            <Button 
              onClick={handleConfirmCancelPlan} 
              disabled={cancellingPlan || !cancelReason.trim()}
              variant="destructive"
              className="gap-2"
            >
              {cancellingPlan && <Loader2 className="w-4 h-4 animate-spin" />}
              <XCircle className="w-4 h-4" />
              Cancel Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default SOFulfillmentPage;
